import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Plus, Loader2, MoreVertical, Ban, Play, Trash2, RotateCcw, Flame, Settings2, Users, LogIn } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  Active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Suspended: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Deleted: "bg-red-500/15 text-red-300 border-red-500/30",
};

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

export default function PlatformAccounts() {
  const nav = useNavigate();
  const { enterAccount } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([api.get("/admin/tenants"), api.get("/admin/stats")]);
      setRows(t.data);
      setStats(s.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/admin/modules").then((r) => setModules(r.data.modules)).catch(() => {});
  }, []);

  const act = async (id, path, label) => {
    setBusyId(id);
    try { await api.post(`/admin/tenants/${id}/${path}`); toast.success(label); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const access = async (t) => {
    setBusyId(t.id);
    try { await enterAccount(t.id); toast.success(`Now inside ${t.name}`); nav("/dashboard"); }
    catch (e) { toast.error(apiError(e)); setBusyId(null); }
  };

  const wipe = async (t) => {
    const confirm = window.prompt(`This PERMANENTLY erases all data for "${t.name}". Type the account name to confirm:`);
    if (confirm === null) return;
    setBusyId(t.id);
    try {
      const { data } = await api.post(`/admin/tenants/${t.id}/wipe`, { confirm });
      const total = Object.values(data.wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`Account wiped — ${total} records erased`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Building2 size={22} className="text-indigo-400" /> Accounts</h1>
          <p className="text-sm text-slate-400 mt-1">Manage every customer account on the platform.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-account-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium transition-colors">
          <Plus size={16} /> New Account
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="Total accounts" value={stats.total_accounts} />
          <Stat label="Active" value={stats.active_accounts} />
          <Stat label="Suspended" value={stats.suspended_accounts} />
          <Stat label="Total users" value={stats.total_users} />
        </div>
      )}

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Account</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Users</th>
              <th className="text-left px-4 py-3 font-medium">Clients</th>
              <th className="text-left px-4 py-3 font-medium">Modules</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" /> Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No accounts yet.</td></tr>
            ) : rows.map((t) => (
              <tr key={t.id} data-testid={`account-row-${t.id}`} className="hover:bg-white/[0.02]">
                <td className="px-4 py-3">
                  <button onClick={() => nav(`/platform/accounts/${t.id}`)} className="font-medium text-white hover:text-indigo-300 text-left">{t.name}</button>
                  <div className="text-xs text-slate-500">{t.last_login ? `Last login ${new Date(t.last_login).toLocaleDateString()}` : "No logins yet"}</div>
                </td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[t.status] || "bg-slate-500/15 text-slate-300 border-slate-500/30"}`} data-testid={`account-status-${t.id}`}>{t.status}</span></td>
                <td className="px-4 py-3 text-slate-300">{t.active_user_count}/{t.user_count}</td>
                <td className="px-4 py-3 text-slate-300">{t.client_count}</td>
                <td className="px-4 py-3 text-slate-300">{(t.enabled_modules || []).length}/{modules.length || 11}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {t.status !== "Deleted" && <button title="Access account" disabled={busyId === t.id} onClick={() => access(t)} data-testid={`access-${t.id}`} className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10"><LogIn size={16} /></button>}
                    <button title="Manage" onClick={() => nav(`/platform/accounts/${t.id}`)} data-testid={`manage-${t.id}`} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10"><Settings2 size={16} /></button>
                    {t.status === "Active" && <button title="Suspend" disabled={busyId === t.id} onClick={() => act(t.id, "suspend", "Account suspended")} data-testid={`suspend-${t.id}`} className="p-1.5 rounded-md text-amber-400 hover:bg-amber-500/10"><Ban size={16} /></button>}
                    {t.status === "Suspended" && <button title="Reactivate" disabled={busyId === t.id} onClick={() => act(t.id, "reactivate", "Account reactivated")} data-testid={`reactivate-${t.id}`} className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10"><Play size={16} /></button>}
                    {t.status !== "Deleted" && <button title="Delete (reversible)" disabled={busyId === t.id} onClick={() => act(t.id, "delete", "Account deleted")} data-testid={`delete-${t.id}`} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10"><Trash2 size={16} /></button>}
                    {t.status === "Deleted" && <button title="Restore" disabled={busyId === t.id} onClick={() => act(t.id, "restore", "Account restored")} data-testid={`restore-${t.id}`} className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10"><RotateCcw size={16} /></button>}
                    <button title="Wipe (permanent)" disabled={busyId === t.id} onClick={() => wipe(t)} data-testid={`wipe-${t.id}`} className="p-1.5 rounded-md text-red-500 hover:bg-red-500/15"><Flame size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateAccountModal modules={modules} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateAccountModal({ modules, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [enabled, setEnabled] = useState(modules.map((m) => m.key));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setEnabled(modules.map((m) => m.key)); }, [modules]);

  const toggle = (k) => setEnabled((e) => (e.includes(k) ? e.filter((x) => x !== k) : [...e, k]));

  const submit = async () => {
    if (!form.name || !form.admin_email || !form.admin_password) return toast.error("Account name, admin email and password are required");
    setSaving(true);
    try {
      await api.post("/admin/tenants", { ...form, enabled_modules: enabled });
      toast.success("Account created");
      onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-white/10 p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="create-account-modal">
        <h2 className="text-lg font-semibold text-white mb-4">New account</h2>
        <div className="space-y-3">
          <Fld label="Account / organisation name"><Inp value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="acc-name" placeholder="Acme Ltd" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Admin full name"><Inp value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} testid="acc-admin-name" placeholder="Jane Doe" /></Fld>
            <Fld label="Admin email"><Inp value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} testid="acc-admin-email" placeholder="admin@acme.com" /></Fld>
          </div>
          <Fld label="Admin temporary password"><Inp value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} testid="acc-admin-password" placeholder="At least 8 characters" /></Fld>
          <div>
            <div className="text-xs text-slate-400 mb-2">Enabled modules</div>
            <div className="grid grid-cols-2 gap-1.5">
              {modules.map((m) => (
                <label key={m.key} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={enabled.includes(m.key)} onChange={() => toggle(m.key)} data-testid={`create-mod-${m.key}`} className="accent-indigo-500 h-4 w-4 rounded" />
                  {m.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="save-account-btn" className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium flex items-center gap-2">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create account
          </button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return <div><div className="text-xs text-slate-400 mb-1">{label}</div>{children}</div>;
}
function Inp({ value, onChange, testid, placeholder }) {
  return <input value={value} data-testid={testid} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-white outline-none focus:border-indigo-400" />;
}
